import { BriefcaseMedical, DollarSign, Stethoscope, User, Users } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import { CommonIcons } from 'common-src/assets/Icons';
import { getClients } from 'common-src/models/Client';
import { getDashboardV2Appointments } from 'common-src/models/DashboardV2Appointment';
import { PlatformStatus } from 'common-src/models/Patient';

import useCustomSelector from 'src/hooks/useCustomSelector';

import {
  DEFAULT_OWNER_STATS,
  DEFAULT_STATS,
  PATHWAY,
  pathwayLabel,
  TASK_STATUS_FILTER_KEYS,
} from '../../constants';
import {
  daysUntil,
  formatClinicianName,
  formatDue,
  formatPatientMrn,
  getClientByAthenaProviderId,
  getFirstVisibleIncompleteSiblingTask,
  getTaskDisplayStatus,
  getTaskPatientGroupKey,
  isLinkedTask,
  isTaskBlockedStatus,
  isTaskEscalatedStatus,
} from '../../helpers';
import { Badge } from '../Badge';
import PatientTaskSearch from '../PatientTaskSearch/PatientTaskSearch';
import TaskDetail from '../TaskDetail';
import TaskRow from '../TaskRow';
import styles from './TasksView.module.scss';

const TasksView = ({
  ownerFilter,
  setOwnerFilter,
  ownerStats,
  activeTeam,
  setActiveTeam,
  statusFilter,
  setStatusFilter,
  patientSearch,
  setPatientSearch,
  stats,
  filtered,
  sortKey,
  sortDirection,
  onSort,
  selectedTask,
  setSelectedTask,
  expandedPatients,
  setExpandedPatients,
  onAssignToMe,
  assigningTaskId,
  onUnassignTask,
  unassigningTaskId,
  onMarkComplete,
  markingTaskId,
  onReassignTask,
  reassigningTaskId,
  onBlockTask,
  blockingTaskId,
  onUnblockTask,
  unblockingTaskId,
  onEscalateTask,
  escalatingTaskId,
  onDeescalateTask,
  deescalatingTaskId,
  currentUserName,
  onReplySent,
}) => {
  const safeStats = stats ?? DEFAULT_STATS;
  const safeOwnerStats = ownerStats ?? DEFAULT_OWNER_STATS;
  const safeFiltered = filtered ?? [];
  const safeOwnerFilter = ownerFilter || 'all';
  const safeActiveTeam = activeTeam || 'all';
  const safeStatusFilter = statusFilter || 'open';
  const [expandedLinkedTasks, setExpandedLinkedTasks] = useState({});
  const clients = useCustomSelector((state) => getClients(state)) || [];
  const appointments = useCustomSelector(getDashboardV2Appointments) || [];

  const getPatientSearchPathway = (patient) => {
    if (patient?.pathway) return patient.pathway;

    const hasActiveCognitive = patient?.cognitiveStatus === PlatformStatus.Active;
    const hasActiveDiabetes = patient?.diabetesStatus === PlatformStatus.Active;

    if (hasActiveCognitive) return PATHWAY.COGNITIVE;
    if (hasActiveDiabetes) return PATHWAY.DIABETES;
    return null;
  };

  const appointmentsById = useMemo(
    () =>
      appointments.reduce((acc, appointment) => {
        const appointmentId = `${appointment?.id || ''}`.trim();
        if (!appointmentId) return acc;
        return { ...acc, [appointmentId]: appointment };
      }, {}),
    [appointments],
  );

  const isPriorityTask = (task) => {
    if (!task?.due) return false;
    return daysUntil(task.due) < 7;
  };

  const getDueTimestamp = (task) =>
    task?.due ? new Date(task.due).getTime() : Number.POSITIVE_INFINITY;

  const getGroupPathway = (tasks, fallbackPathway) => {
    const pathwayCounts = tasks.reduce((acc, task) => {
      const pathway = task?.patient?.pathway;
      if (!pathway) return acc;
      acc[pathway] = (acc[pathway] ?? 0) + 1;
      return acc;
    }, {});

    const topPathway = Object.entries(pathwayCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    return topPathway ?? fallbackPathway ?? null;
  };

  const getTaskClinician = (task) => {
    const appointmentId = `${task?.externalObjectId || ''}`.trim();
    if (!appointmentId) return null;

    const linkedAppointment = appointmentsById[appointmentId];
    const clinicianClient = getClientByAthenaProviderId(
      clients,
      linkedAppointment?.providerAthenaId,
    );
    const clinician = formatClinicianName(clinicianClient);
    return clinician === '-' ? null : clinician;
  };

  const getGroupClinician = (tasks = [], fallbackClinician = null) => {
    const clinician = tasks.map(getTaskClinician).find(Boolean);
    return clinician ?? fallbackClinician ?? null;
  };

  const getTaskPhase = (task) => {
    const taskPhase = task?.phase;
    if (taskPhase != null && `${taskPhase}`.trim() !== '') return taskPhase;
    return null;
  };

  const getGroupPhase = (tasks = [], fallbackPhase = null) => {
    const phase = tasks
      .map(getTaskPhase)
      .find((value) => value != null && `${value}`.trim() !== '');
    return phase ?? fallbackPhase ?? null;
  };

  const patientSearchMetadataById = useMemo(() => {
    const groupedPatients = safeFiltered.reduce((acc, task) => {
      const patientId = `${task?.patientId ?? task?.patient?.id ?? ''}`.trim();
      if (!patientId) return acc;

      if (!acc[patientId]) {
        acc[patientId] = {
          patient: task?.patient ?? {},
          tasks: [],
        };
      }

      acc[patientId].tasks.push(task);
      return acc;
    }, {});

    return Object.entries(groupedPatients).reduce((acc, [patientId, group]) => {
      const fallbackPhase = group.patient?.phase ?? 1;
      acc[patientId] = {
        pathway: getGroupPathway(group.tasks, group.patient?.pathway ?? null),
        phase: getGroupPhase(group.tasks, fallbackPhase),
        clinician: getGroupClinician(group.tasks, group.patient?.clinician ?? null),
        patientMrn: formatPatientMrn(group.patient?.mrn) || (patientId ? `MRN-${patientId}` : null),
      };
      return acc;
    }, {});
  }, [clients, safeFiltered, appointmentsById]);

  const eligiblePatientIds = useMemo(
    () => [
      ...new Set(
        safeFiltered
          .map((task) => `${task?.patientId ?? task?.patient?.id ?? ''}`.trim())
          .filter(Boolean),
      ),
    ],
    [safeFiltered],
  );

  const getPatientSearchLabel = (patient) => {
    const firstName = patient.preferredName || patient.firstName;
    const { lastName } = patient;
    const name = `${firstName} ${lastName}`.trim();
    const metadata = patientSearchMetadataById[`${patient?.id ?? ''}`] ?? {};
    const mrn =
      metadata.patientMrn || formatPatientMrn(patient?.mrn) || formatPatientMrn(patient?.id);

    return `${name} ${mrn || ''}`.trim();
  };

  const buildLinkedTaskStructure = (tasks = []) => {
    const childIds = new Set();
    const childTasksByParentId = new Map();
    const parentIdByChildId = new Map();

    tasks.forEach((task) => {
      if (!isLinkedTask(task)) return;
      if (getTaskDisplayStatus(task) === 'done') return;

      const parentTask = getFirstVisibleIncompleteSiblingTask(task, tasks);
      if (!parentTask?.id) return;

      const parentId = `${parentTask.id}`;
      childIds.add(`${task.id}`);
      parentIdByChildId.set(`${task.id}`, parentId);

      if (!childTasksByParentId.has(parentId)) {
        childTasksByParentId.set(parentId, []);
      }

      childTasksByParentId.get(parentId).push(task);
    });

    return {
      childIds,
      childTasksByParentId,
      parentIdByChildId,
      parentChildCount: new Map(
        Array.from(childTasksByParentId.entries()).map(([parentId, childTasks]) => [
          parentId,
          childTasks.length,
        ]),
      ),
    };
  };

  const buildRenderableSectionTasks = (sectionTasks = [], linkedTaskStructure) => {
    const { childIds, childTasksByParentId, parentIdByChildId } = linkedTaskStructure;
    const renderableTasks = [];
    const addedTaskIds = new Set();

    sectionTasks.forEach((task) => {
      const taskId = `${task?.id || ''}`;
      if (!taskId) return;
      if (addedTaskIds.has(taskId)) return;

      if (childIds.has(taskId)) {
        const parentId = parentIdByChildId.get(taskId);
        const isExpanded = !!expandedLinkedTasks[parentId];
        if (!isExpanded) return;
      }

      renderableTasks.push(task);
      addedTaskIds.add(taskId);

      const childTasks = childTasksByParentId.get(taskId) || [];
      const isExpanded = !!expandedLinkedTasks[taskId];

      if (!childTasks.length || !isExpanded) return;

      childTasks.forEach((childTask) => {
        const childTaskId = `${childTask?.id || ''}`;
        if (!childTaskId || addedTaskIds.has(childTaskId)) return;
        renderableTasks.push(childTask);
        addedTaskIds.add(childTaskId);
      });
    });

    return renderableTasks;
  };

  const patientGroups = useMemo(() => {
    const grouped = new Map();

    safeFiltered.forEach((task) => {
      const key = getTaskPatientGroupKey(task);
      const patient = task?.patient ?? {};
      const group = grouped.get(key) ?? {
        key,
        patient,
        patientName: patient?.name || 'Unknown Patient',
        patientMrn:
          formatPatientMrn(patient?.mrn) ||
          (task?.patientId != null
            ? `MRN-${task.patientId}`
            : task?.patient?.id != null
              ? `MRN-${task.patient.id}`
              : null),
        patientPhase: getTaskPhase(task),
        patientClinician: getTaskClinician(task),
        pathway: patient?.pathway ?? null,
        tasks: [],
      };

      group.tasks.push(task);
      grouped.set(key, group);
    });

    return [...grouped.values()]
      .map((group) => {
        const linkedTaskStructure = buildLinkedTaskStructure(group.tasks);
        const priorityTasks = group.tasks.filter((task) => {
          if (isLinkedTask(task) && getTaskDisplayStatus(task) !== 'done') return false;
          return isPriorityTask(task);
        });
        const futureTasks = group.tasks.filter((task) => {
          if (isLinkedTask(task) && getTaskDisplayStatus(task) !== 'done') return false;
          return !isPriorityTask(task);
        });
        const criticalCount = group.tasks.filter((task) => task.priority === 'critical').length;
        const blockedCount = group.tasks.filter((task) => isTaskBlockedStatus(task)).length;
        const escalatedCount = group.tasks.filter((task) => isTaskEscalatedStatus(task)).length;
        const nextPriorityDue = priorityTasks.reduce(
          (min, task) => Math.min(min, getDueTimestamp(task)),
          Number.POSITIVE_INFINITY,
        );
        const nextDue = group.tasks.reduce(
          (min, task) => Math.min(min, getDueTimestamp(task)),
          Number.POSITIVE_INFINITY,
        );
        const nextDueTask = group.tasks.find(
          (task) => getDueTimestamp(task) === nextDue && Number.isFinite(nextDue),
        );

        return {
          ...group,
          patientPhase: getGroupPhase(group.tasks, group.patientPhase),
          patientClinician: getGroupClinician(group.tasks, group.patientClinician),
          pathway: getGroupPathway(group.tasks, group.pathway),
          priorityTasks,
          futureTasks,
          linkedTaskStructure,
          criticalCount,
          blockedCount,
          escalatedCount,
          nowCount: priorityTasks.length,
          laterCount: futureTasks.length,
          nextPriorityDue,
          nextDue,
          nextDueLabel: nextDueTask?.due ? formatDue(nextDueTask.due).text : '—',
        };
      })
      .sort((a, b) => {
        if (a.nextPriorityDue !== b.nextPriorityDue) return a.nextPriorityDue - b.nextPriorityDue;
        if (a.nextDue !== b.nextDue) return a.nextDue - b.nextDue;
        return a.patientName.localeCompare(b.patientName);
      });
  }, [safeFiltered]);

  const prioritySummary = useMemo(
    () =>
      safeFiltered.reduce(
        (acc, task) => {
          const priority = task?.priority;
          if (priority && Object.prototype.hasOwnProperty.call(acc, priority)) {
            acc[priority] += 1;
          }
          return acc;
        },
        {
          critical: 0,
          high: 0,
          medium: 0,
          low: 0,
        },
      ),
    [safeFiltered],
  );

  const togglePatient = (patientKey) => {
    setExpandedPatients((prev) => ({
      ...prev,
      [patientKey]: !prev[patientKey],
    }));
  };

  const expandAllPatients = () => {
    setExpandedPatients(patientGroups.reduce((acc, group) => ({ ...acc, [group.key]: true }), {}));
  };

  const collapseAllPatients = () => {
    setExpandedPatients({});
  };

  const toggleLinkedTasks = (taskId) => {
    const normalizedTaskId = `${taskId || ''}`;
    if (!normalizedTaskId) return;

    setExpandedLinkedTasks((prev) => ({
      ...prev,
      [normalizedTaskId]: !prev[normalizedTaskId],
    }));
  };

  const sortIcon = (key) => {
    const isActive = sortKey === key;
    return (
      <img
        className={styles.sortIcon}
        style={{ transform: isActive && sortDirection === 'asc' ? 'rotate(-180deg)' : 'none' }}
        src={CommonIcons.sortDownIcon}
        alt={isActive ? `sort-${sortDirection}` : 'sort'}
      />
    );
  };

  const ownerTabs = [
    { key: 'all', label: 'All', count: safeOwnerStats.all },
    { key: 'my', label: 'My Tasks', count: safeOwnerStats.my },
    { key: 'unassigned', label: 'Unassigned', count: safeOwnerStats.unassigned },
  ];

  const teamTabs = [
    { key: 'all', label: 'All', count: safeStats.total, icon: Users },
    { key: 'member', label: 'Member', count: safeStats.member, icon: User },
    { key: 'ops', label: 'Ops', count: safeStats.ops, icon: BriefcaseMedical },
    {
      key: 'clinical',
      label: 'Clinical',
      count: safeStats.clinical,
      icon: Stethoscope,
    },
    { key: 'rcm', label: 'RCM', count: safeStats.rcm, icon: DollarSign },
  ];

  const statusTabs = [
    { key: 'open', label: 'Open' },
    { key: 'critical', label: `Critical ${safeStats.critical}` },
    { key: 'blocked', label: `Blocked ${safeStats.blocked}` },
    { key: 'escalated', label: `Escalated ${safeStats.escalated}` },
    { key: 'messages', label: `Messages ${safeStats.messages}` },
    { key: 'assessments', label: `Assessments ${safeStats.assessments}` },
  ];

  return (
    <>
      <div className={styles.teamTabsBar}>
        <div className={styles.teamTabsGroup}>
          {teamTabs.map((team) => (
            <button
              key={team.key}
              type="button"
              onClick={() => setActiveTeam(team.key)}
              className={`${styles.teamTabButton} ${
                safeActiveTeam === team.key ? styles.teamTabActive : ''
              }`}
            >
              <span className={styles.teamTabIcon}>
                <team.icon size={14} strokeWidth={1.9} aria-hidden="true" />
              </span>
              {team.label}
              <span className={styles.teamTabCount}>{team.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            {ownerTabs.map((ownerTab) => (
              <button
                key={ownerTab.key}
                type="button"
                onClick={() => setOwnerFilter(ownerTab.key)}
                className={`${styles.filterButton} ${
                  safeOwnerFilter === ownerTab.key ? styles.filterActive : ''
                }`}
              >
                {ownerTab.label}
                <span className={styles.filterCount}>{ownerTab.count}</span>
              </button>
            ))}
          </div>
          <div className={styles.filterDivider} />
          <div className={styles.filterGroup}>
            {statusTabs.map((status) => (
              <button
                key={status.key}
                type="button"
                onClick={() => setStatusFilter(status.key)}
                className={`${styles.filterButton} ${
                  safeStatusFilter === status.key ? styles.filterActive : ''
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
          <div className={styles.filterActions}>
            <PatientTaskSearch
              eligiblePatientIds={eligiblePatientIds}
              patientSearch={patientSearch}
              setPatientSearch={setPatientSearch}
              getPatientSearchLabel={getPatientSearchLabel}
              getPatientSearchPathway={getPatientSearchPathway}
              patientSearchMetadataById={patientSearchMetadataById}
            />
            <button
              type="button"
              onClick={expandAllPatients}
              className={styles.filterActionButton}
              disabled={!patientGroups.length}
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAllPatients}
              className={styles.filterActionButton}
              disabled={!patientGroups.length}
            >
              Collapse
            </button>
          </div>
        </div>
      </div>

      <div className={`${styles.layout} ${selectedTask ? styles.layoutWithDetail : ''}`}>
        <div className={styles.listCard}>
          <div className={styles.summaryBar}>
            <div className={styles.summaryMeta}>
              <span className={styles.summaryText}>
                {patientGroups.length} patients · {safeFiltered.length} tasks
              </span>
              <div className={styles.prioritySummary}>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryCritical}`}>
                  Critical: {prioritySummary.critical}
                </span>
                <span className={styles.prioritySummaryDivider}>·</span>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryHigh}`}>
                  High: {prioritySummary.high}
                </span>
                <span className={styles.prioritySummaryDivider}>·</span>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryMedium}`}>
                  Medium: {prioritySummary.medium}
                </span>
                <span className={styles.prioritySummaryDivider}>·</span>
                <span className={`${styles.prioritySummaryItem} ${styles.prioritySummaryLow}`}>
                  Low: {prioritySummary.low}
                </span>
              </div>
            </div>
            <div className={styles.summaryActions}>
              <span className={styles.summarySort}>Sorted by priority</span>
            </div>
          </div>

          {patientGroups.map((group) => {
            const isExpanded = !!expandedPatients[group.key];
            const pathway = pathwayLabel[group.pathway] ?? group.pathway;

            return (
              <div key={group.key} className={styles.patientGroup}>
                <button
                  type="button"
                  className={styles.patientRow}
                  onClick={() => togglePatient(group.key)}
                >
                  <span className={styles.patientChevron}>{isExpanded ? '⌄' : '›'}</span>
                  <span className={styles.patientMarker} />
                  <div className={styles.patientMain}>
                    <div className={styles.patientTopRow}>
                      <span className={styles.patientName}>{group.patientName}</span>
                      {group.patientMrn ? (
                        <span className={styles.patientMetaMuted}>{group.patientMrn}</span>
                      ) : null}
                    </div>
                    <div className={styles.patientMetaRow}>
                      {group.patientPhase != null ? (
                        <span className={styles.patientMetaText}>Phase {group.patientPhase}</span>
                      ) : null}
                      {group.patientClinician ? (
                        <span className={styles.patientMetaText}>{group.patientClinician}</span>
                      ) : null}
                      {pathway ? (
                        <Badge variant="outline" className={styles.pathwayBadge}>
                          {pathway}
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.patientPills}>
                    {group.criticalCount ? (
                      <Badge variant="critical" className={styles.patientSummaryBadge}>
                        {group.criticalCount} critical
                      </Badge>
                    ) : null}
                    {group.blockedCount ? (
                      <Badge variant="critical" className={styles.patientSummaryBadge}>
                        {group.blockedCount} blocked
                      </Badge>
                    ) : null}
                    {group.escalatedCount ? (
                      <Badge variant="escalated" className={styles.patientSummaryBadge}>
                        {group.escalatedCount} escalated
                      </Badge>
                    ) : null}
                    {group.nowCount || group.laterCount ? (
                      <span className={styles.summaryPillGroup}>
                        {group.nowCount ? (
                          <span
                            className={`${styles.summaryPill} ${styles.summaryPillDark} ${
                              group.laterCount ? styles.summaryPillGrouped : ''
                            }`}
                          >
                            {group.nowCount} now
                          </span>
                        ) : null}
                        {group.laterCount ? (
                          <span
                            className={`${styles.summaryPill} ${
                              group.nowCount
                                ? `${styles.summaryPillGrouped} ${styles.summaryPillGroupedTrailing}`
                                : ''
                            }`}
                          >
                            {group.laterCount} later
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                  <span className={styles.patientDue}>{group.nextDueLabel}</span>
                </button>

                {isExpanded ? (
                  <div className={styles.patientExpanded}>
                    {[
                      {
                        key: 'priority',
                        label: 'Priority',
                        tasks: buildRenderableSectionTasks(
                          group.priorityTasks,
                          group.linkedTaskStructure,
                        ),
                      },
                      {
                        key: 'future',
                        label: 'Future',
                        tasks: buildRenderableSectionTasks(
                          group.futureTasks,
                          group.linkedTaskStructure,
                        ),
                      },
                    ].map((section, index) => {
                      if (!section.tasks.length) return null;

                      return (
                        <div key={section.key} className={styles.bucketSection}>
                          <div className={styles.bucketHeader}>
                            <span
                              className={`${styles.bucketIndex} ${
                                section.key === 'priority' ? styles.bucketIndexDark : ''
                              }`}
                            >
                              {index + 1}
                            </span>
                            <span className={styles.bucketLabel}>{section.label}</span>
                            <span className={styles.bucketCount}>{section.tasks.length}</span>
                          </div>
                          <div className={styles.bucketRows}>
                            <div className={styles.tableHeader}>
                              <span />
                              <span className={styles.headerCell}>Task</span>
                              <span className={styles.headerCell} />
                              <button
                                type="button"
                                onClick={() => onSort?.('status')}
                                className={`${styles.headerCell} ${styles.sortButton} ${
                                  sortKey === 'status' ? styles.sortActive : ''
                                }`}
                              >
                                Status
                                {sortIcon('status')}
                              </button>
                              <button
                                type="button"
                                onClick={() => onSort?.('priority')}
                                className={`${styles.headerCell} ${styles.sortButton} ${
                                  sortKey === 'priority' ? styles.sortActive : ''
                                }`}
                              >
                                Priority
                                {sortIcon('priority')}
                              </button>
                              <button
                                type="button"
                                onClick={() => onSort?.('due')}
                                className={`${styles.headerCell} ${styles.sortButton} ${
                                  styles.headerDue
                                } ${sortKey === 'due' ? styles.sortActive : ''}`}
                              >
                                Due
                                {sortIcon('due')}
                              </button>
                            </div>
                            {section.tasks.map((task) => (
                              <TaskRow
                                key={task.id}
                                task={task}
                                isLinkedChild={group.linkedTaskStructure.childIds.has(`${task.id}`)}
                                linkedParentTitle={
                                  group.linkedTaskStructure.childIds.has(`${task.id}`)
                                    ? (group.tasks.find(
                                        (candidateTask) =>
                                          `${candidateTask?.id}` ===
                                          group.linkedTaskStructure.parentIdByChildId.get(
                                            `${task.id}`,
                                          ),
                                      )?.title ?? null)
                                    : null
                                }
                                linkedChildCount={
                                  group.linkedTaskStructure.parentChildCount.get(`${task.id}`) ?? 0
                                }
                                linkedExpanded={!!expandedLinkedTasks[`${task.id}`]}
                                onToggleLinked={toggleLinkedTasks}
                                selected={selectedTask?.id === task.id}
                                onSelect={setSelectedTask}
                                onAssignToMe={onAssignToMe}
                                isAssigning={assigningTaskId === task.id}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}

          {!patientGroups.length && <div className={styles.emptyState}>No tasks match filters</div>}
        </div>

        {selectedTask && (
          <div className={styles.detailPanel}>
            <TaskDetail
              task={selectedTask}
              onClose={() => setSelectedTask(null)}
              onAssignToMe={onAssignToMe}
              isAssigning={assigningTaskId === selectedTask.id}
              onUnassign={onUnassignTask}
              isUnassigning={unassigningTaskId === selectedTask.id}
              onMarkComplete={onMarkComplete}
              isMarking={markingTaskId === selectedTask.id}
              onReassign={onReassignTask}
              isReassigning={reassigningTaskId === selectedTask.id}
              onBlockTask={onBlockTask}
              isBlocking={blockingTaskId === selectedTask.id}
              onUnblockTask={onUnblockTask}
              isUnblocking={unblockingTaskId === selectedTask.id}
              onEscalateTask={onEscalateTask}
              isEscalating={escalatingTaskId === selectedTask.id}
              onDeescalateTask={onDeescalateTask}
              isDeescalating={deescalatingTaskId === selectedTask.id}
              currentUserName={currentUserName}
              onReplySent={onReplySent}
            />
          </div>
        )}
      </div>
    </>
  );
};

TasksView.propTypes = {
  ownerFilter: PropTypes.oneOf(['all', 'my', 'unassigned']),
  setOwnerFilter: PropTypes.func,
  ownerStats: PropTypes.shape({
    all: PropTypes.number,
    my: PropTypes.number,
    unassigned: PropTypes.number,
  }),
  activeTeam: PropTypes.oneOf(['all', 'member', 'ops', 'clinical', 'rcm']),
  setActiveTeam: PropTypes.func,
  statusFilter: PropTypes.oneOf(TASK_STATUS_FILTER_KEYS),
  setStatusFilter: PropTypes.func,
  patientSearch: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    preferredName: PropTypes.string,
  }),
  setPatientSearch: PropTypes.func,
  stats: PropTypes.shape({
    total: PropTypes.number,
    member: PropTypes.number,
    ops: PropTypes.number,
    clinical: PropTypes.number,
    rcm: PropTypes.number,
    cognitive: PropTypes.number,
    kisunla: PropTypes.number,
    'leqembi-iv': PropTypes.number,
    'leqembi-sc': PropTypes.number,
    critical: PropTypes.number,
    blocked: PropTypes.number,
    escalated: PropTypes.number,
    messages: PropTypes.number,
    assessments: PropTypes.number,
  }),
  filtered: PropTypes.arrayOf(PropTypes.object),
  sortKey: PropTypes.oneOf(['due', 'status', 'priority']),
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
  selectedTask: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  setSelectedTask: PropTypes.func,
  expandedPatients: PropTypes.objectOf(PropTypes.bool),
  setExpandedPatients: PropTypes.func,
  onAssignToMe: PropTypes.func,
  assigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUnassignTask: PropTypes.func,
  unassigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onMarkComplete: PropTypes.func,
  markingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onReassignTask: PropTypes.func,
  reassigningTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBlockTask: PropTypes.func,
  blockingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUnblockTask: PropTypes.func,
  unblockingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onEscalateTask: PropTypes.func,
  escalatingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDeescalateTask: PropTypes.func,
  deescalatingTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserName: PropTypes.string,
  onReplySent: PropTypes.func,
};

export default TasksView;
